import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Honest placeholder for Coach Pro while subscriptions aren't launched. */
export function CoachProComingSoonDialog({ open, onOpenChange, defaultEmail }: { open: boolean; onOpenChange: (o: boolean) => void; defaultEmail: string }) {
  const [email, setEmail] = useState(defaultEmail);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) { toast.error('Enter a valid email address'); return; }
    setSaving(true);
    const { error } = await supabase.from('waitlist').insert({ email: value });
    setSaving(false);
    // A duplicate means they're already on the list — treat as success.
    if (error && error.code !== '23505') { toast.error("Couldn't save your email — please try again"); return; }
    toast.success("You're on the list — we'll email you when Coach Pro launches.");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" />Coach Pro is coming soon</DialogTitle>
          <DialogDescription>
            Sportara is in beta, so Coaches Hub is free for you right now — there's nothing to buy yet. Leave your email if you'd like a heads-up when Coach Pro launches.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" aria-label="Email for Coach Pro updates" />
          <Button type="submit" className="w-full" disabled={saving}>
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Notify me'}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>Maybe later</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
